import React, { Fragment, useEffect, useRef, useState } from 'react';

import { SubmitHandler } from 'react-hook-form';
import { useApolloClient } from '@apollo/client/react';
import {
  Alert,
  Box,
  Button,
  Divider,
  Grid,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { Theme, useTheme } from '@mui/material/styles';
import JsCookie from 'js-cookie';
import _ from 'lodash';
import logger from 'logger';
import { DateTime } from 'luxon';
import { getNextConfigValues } from 'next-config';
import { useRouter } from 'next/router';
import useAnalytics from 'src/analytics/useAnalytics';
import CartFooterAndSummary from 'src/components/Menu/CartFooterAndSummary';
import SoldOutConfirmationModal from 'src/components/Menu/SoldOutConfirmationModal';
import Catalog from 'src/components/Catalog';
import CatalogAnchorNav from 'src/components/CatalogAnchorNav';
import Dropdown from 'src/components/Dropdown';
import Layout from 'src/components/Layout';
import Loading from 'src/components/Loading';
import FunnelNavBarHeader from 'src/components/NavBarHeader/FunnelNavBarHeader';
import UserContext from 'src/context/user';
import useNonNullContext from 'src/hooks/useNonNullContext';
import { CatalogsQuery } from 'src/ts/@types/generated/graphql/catalog-service/graphql';
import { Sorter } from 'src/utils/filters/sorters';
import { getCartDelivery } from 'src/utils/carts';
import { getSoldOutItemMap } from 'src/utils/catalog-items';
import {
  CART_ID_COOKIE_KEY,
  DISCOUNT_CODE_PARAM_KEY,
  REFERRAL_CODE_PARAM_KEY,
} from 'src/utils/constants';
import { findOrThrow } from 'src/utils/generic';

import { FunnelCart } from 'src/ts/types/cart';
import {
  DESKTOP_MENU_NAV_BAR_HEIGHT,
  MOBILE_MENU_NAV_BAR_HEIGHT,
} from '../../utils/constants';
import PromoCodeBanner from '../PromoCodeBanner';
import ZipCodeModal from './ZipCodeModal';
import { DisplayFilters } from 'src/utils/filters/display-filters';
import SortersDropdown from '../Sorters/SortersDropdown';
import FilterPanel from './FilterPanel';
import { menuSorters } from './_utils/sorters';
import {
  addVariantToCartDelivery,
  removeVariantFromCartDelivery,
} from './_utils/add-remove-variants';
import { Plan } from 'src/services/cart-order/plans-service';

const {
  publicRuntimeConfig: { frontendUrls },
} = getNextConfigValues();

interface Props {
  cart: FunnelCart | null;
  setCart: React.Dispatch<React.SetStateAction<FunnelCart | null>>;
  catalogs: NonNullable<CatalogsQuery['catalogs'][0]>[];
  featuredSkuGroups: string[][];
  isLoading: boolean;
  isZipCodeModalOpen: boolean;
  setIsZipCodeModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  handleZipCodeModalSubmit: SubmitHandler<{
    zipCode: string;
    email?: string;
    name?: string;
  }>;
  isInvalidZipCode: boolean;
  displayFilters: DisplayFilters;
  selectedFilters: DisplayFilters;
  setSelectedFilters: React.Dispatch<React.SetStateAction<DisplayFilters>>;
  selectedSorter: Sorter;
  setSelectedSorter: React.Dispatch<React.SetStateAction<Sorter>>;
  menuEntryOverride?: 'ZIP' | 'EMAIL_ZIP' | 'FULL';
  selectedPlan?: Plan;
}

const MenuComponent: React.FC<Props> = ({
  cart,
  setCart,
  catalogs,
  featuredSkuGroups,
  isLoading,
  isZipCodeModalOpen,
  setIsZipCodeModalOpen,
  handleZipCodeModalSubmit,
  isInvalidZipCode,
  displayFilters,
  selectedFilters,
  setSelectedFilters,
  selectedSorter,
  setSelectedSorter,
  menuEntryOverride,
  selectedPlan,
}) => {
  const router = useRouter();
  const apolloClient = useApolloClient();
  const theme = useTheme();
  const { user } = useNonNullContext(UserContext);
  const { track } = useAnalytics();
  const [indexOfActiveCatalog, setIndexOfActiveCatalog] = useState(0);
  const isSmallScreen = useMediaQuery<Theme>((theme) =>
    theme.breakpoints.down('md'),
  );

  const [errorMsg, setErrorMsg] = useState<string>();
  const catalogRefs = useRef<Array<HTMLDivElement>>([]);
  const [soldOutItemVariantMap, setSoldOutItemVariantMap] = useState<
    Record<number, Array<number>>
  >({});

  const [soldOutDialogOpen, setSoldOutDialogOpen] = useState(false);
  const [pendingSoldOutRemoval, setPendingSoldOutRemoval] = useState<{
    productVariantId: number;
    cartDeliveryId: number;
  } | null>(null);

  useEffect(() => {
    if (catalogs.length) {
      catalogRefs.current = catalogRefs.current.slice(0, catalogs.length);
    }
  }, [catalogs]);

  useEffect(() => {
    setSoldOutItemVariantMap(getSoldOutItemMap(catalogs));
  }, [catalogs]);

  const onlyOneCatalog = catalogs.length === 1;

  const catalogDropdownOptions = cart?.cartDeliveries?.map((delivery, idx) => {
    // This date fallback logic is only needed for the blurred "loading" catalog displayed while the zip modal is open since no cart exists yet.
    const deliveryDate = DateTime.fromISO(
      delivery.estimatedDeliveryDate || catalogs[idx]?.eligibleDeliveryDates[0],
      {
        zone: 'utc',
      },
    );
    const formattedDeliveryDateString = deliveryDate.toLocaleString({
      weekday: 'short',
      month: 'short',
      day: 'numeric',
    });

    return {
      label: `Delivery on ${formattedDeliveryDateString}`,
    };
  });

  async function addMealToCart(
    productVariantId: number,
    cartDeliveryId?: number,
  ) {
    // Check if cart is empty before adding meal. If it is, we'll track that a meal has been added to the cart for the first time.
    const totalMealsBeforeAdd =
      cart?.cartDeliveries.reduce(
        (total, delivery) =>
          total +
          delivery.lineItems.reduce(
            (deliveryTotal, lineItem) => deliveryTotal + lineItem.quantity,
            0,
          ),
        0,
      ) ?? 0;

    try {
      const cartDelivery = findOrThrow(
        cart!.cartDeliveries,
        (delivery) => delivery.id === cartDeliveryId,
      );
      const addVariantResult = await addVariantToCartDelivery({
        apolloClient,
        args: {
          cartUUID: cart?.uuid,
          cartDeliveryId: cartDelivery?.id,
          variantInput: {
            variantId: productVariantId,
            quantity: 1,
          },
        },
        previewFunnelDiscountCode: !user,
      });

      if (addVariantResult.success) {
        // Track first meal added to cart
        if (totalMealsBeforeAdd === 0) {
          track('Meal Added to Cart', {});
        }
        setCart(addVariantResult.cart);
        setErrorMsg(undefined);
      } else {
        if (addVariantResult.error === 'STATUS_ERROR') {
          JsCookie.remove(CART_ID_COOKIE_KEY);
          router.push({
            pathname: '/get-started',
            query: { purchasedCart: true },
          });
        } else if (addVariantResult.error === 'SOLD_OUT') {
          setErrorMsg(
            'Unable to update cart. Attempted to add a sold out item.',
          );
        } else if (addVariantResult.error === 'UNKNOWN') {
          setErrorMsg('An unknown error occurred, please try again.');
        }
      }
    } catch (err: any) {
      setErrorMsg('An unknown error occurred, please try again.');

      logger.error({
        noticable: err,
        msg: 'Failed to updated cartDelivery',
        extra: {
          context: {
            productVariantId,
            cartDeliveryId,
            zipcode: cart?.funnelData.zip,
          },
        },
      });
    }
  }

  async function removeMealWithSoldOutCheck(
    productVariantId: number,
    cartDeliveryId: number,
  ) {
    const cartDelivery = cart?.cartDeliveries.find((cartDelivery) => {
      return cartDelivery.id === cartDeliveryId;
    });

    if (!cartDelivery) {
      setErrorMsg('Unable to remove item, please try again');
      logger.error({
        msg: 'FailedToFindCartDeliveryToRemoveItem',
        extra: {
          context: {
            productVariantId,
            cartDeliveryId,
            cart,
          },
        },
      });

      return;
    }

    const itemIsSoldOut =
      Object.keys(soldOutItemVariantMap).length > 0 &&
      soldOutItemVariantMap[cartDelivery.deliveryDate].includes(
        productVariantId,
      );

    // If the item isn't sold out, just do the regular removal.
    if (!itemIsSoldOut) {
      await removeMealFromCart(productVariantId, cartDeliveryId);
      return;
    }

    // Otherwise, need to open up the confirmation modal
    setPendingSoldOutRemoval({ productVariantId, cartDeliveryId });
    setSoldOutDialogOpen(true);
  }

  async function removeMealFromCart(
    productVariantId: number,
    cartDeliveryId: number,
  ) {
    try {
      const cartDelivery = findOrThrow(
        cart!.cartDeliveries,
        (delivery) => delivery.id === cartDeliveryId,
      );

      const removeVariantResult = await removeVariantFromCartDelivery({
        apolloClient,
        args: {
          cartUUID: cart?.uuid,
          cartDeliveryId: cartDelivery.id,
          variantInput: {
            variantId: productVariantId,
            quantity: 1,
          },
        },
        previewFunnelDiscountCode: !user,
      });

      if (removeVariantResult.success) {
        setCart(removeVariantResult.cart);
      } else {
        if (removeVariantResult.error === 'STATUS_ERROR') {
          JsCookie.remove(CART_ID_COOKIE_KEY);
          router.push({
            pathname: '/get-started',
            query: { purchasedCart: true },
          });
        } else if (removeVariantResult.error === 'UNKNOWN') {
          setErrorMsg('An unknown error occurred, please try again.');
        }
      }
    } catch (err: any) {
      setErrorMsg('An unknown error occurred, please try again.');

      logger.error({
        noticable: err,
        msg: 'Failed to update cartDelivery',
        extra: {
          context: {
            productVariantId,
            cartDeliveryId,
            zipcode: cart?.funnelData.zip,
          },
        },
      });
    }
  }

  // See discussion about using smooth scroll: https://github.com/powersupplyhq/ecommerce-platform-frontend/pull/235#discussion_r864183923
  const scrollToCatalog = (catalogIdx: number, isMobile: boolean): void => {
    if (isMobile) {
      window.scrollTo({ top: 0, left: 0 });
      return;
    }

    const ref = catalogRefs.current[catalogIdx];
    if (catalogIdx === 0) {
      window.scrollTo({ top: 0, left: 0 });
    } else {
      const navHeight = isMobile
        ? MOBILE_MENU_NAV_BAR_HEIGHT
        : DESKTOP_MENU_NAV_BAR_HEIGHT;
      window.scrollTo({
        top: ref.offsetTop - navHeight + 2,
        left: 0,
      });
    }
  };

  // Mobile UX is a bit different from desktop in that mobile is a tabbed experience,
  //   while desktop is a fully-scrollable experience. This bit of logic handles that.
  const catalogsToDisplay = isSmallScreen
    ? catalogs.slice(indexOfActiveCatalog, indexOfActiveCatalog + 1)
    : catalogs;

  const onDrawerContinueButtonClick = async () => {
    if (!cart) {
      return;
    }

    window.location.href = `${frontendUrls.checkout}?${new URLSearchParams({
      [CART_ID_COOKIE_KEY]: cart?.uuid,
      ...(cart?.funnelData.params.discountCode && {
        [DISCOUNT_CODE_PARAM_KEY]: cart.funnelData.params.discountCode,
      }),
    }).toString()}`;
  };

  const totalFilteredCatalogItems = _.sumBy(
    catalogs,
    (catalog) => catalog.items.length,
  );

  const resetSelectedFilters = () => {
    setSelectedFilters((prevState) => {
      return {
        macros: prevState.macros?.map((macroFilter) => ({
          ...macroFilter,
          min: 0,
          max: null,
        })),
        ingredientsToAvoid: [],
        chefNames: [],
        tagCategories: prevState.tagCategories?.map((tagCategory) => ({
          ...tagCategory,
          tags: [],
        })),
      };
    });
  };

  return (
    <Fragment>
      <FunnelNavBarHeader
        zipCode={{
          cartZipCode: cart?.funnelData?.zip,
          setZipCodeModalOpen: setIsZipCodeModalOpen,
        }}
      />
      {errorMsg && (
        <Alert
          severity="error"
          sx={{
            backgroundColor: theme.palette.error.main,
            color: theme.palette.common.white,
            marginTop: '4rem',
            marginBottom: '-4rem',
            position: 'relative',
            ['.MuiAlert-icon']: { color: theme.palette.common.white },
          }}
        >
          {errorMsg}
        </Alert>
      )}
      <Box sx={{ marginBottom: '3rem' }}>
        <Layout
          hideHeader
          isShowingCart={!!cart}
          contentCssOverrides={{
            maxWidth: '1312px',
            padding: '5rem 16px 5rem 16px',
          }}
        >
          {cart && (
            <CartFooterAndSummary
              cart={cart}
              setCart={setCart}
              addMealToCart={addMealToCart}
              removeMealFromCart={removeMealWithSoldOutCheck}
              onContinueButtonClick={onDrawerContinueButtonClick}
              soldOutItemVariantMap={soldOutItemVariantMap}
              selectedPlan={selectedPlan}
              createdPopulatedFromPersonalizedPlan={
                cart.createdFromPersonalizedPlan
              }
            />
          )}
          {isSmallScreen && catalogs?.length >= 1 ? (
            <Grid
              sx={{
                position: 'sticky',
                top: '3.5rem',
                zIndex: 1000,
                mx: '-16px',
                width: 'calc(100% + 32px)',
              }}
              size={12}
            >
              <Dropdown
                height="57px" // in px to match Cart/MobileDrawer's "drawerBleeding"
                options={catalogDropdownOptions}
                onlyOneCatalog={onlyOneCatalog}
                onChange={(idx: number) => {
                  setIndexOfActiveCatalog(idx);
                  scrollToCatalog(idx, isSmallScreen);
                }}
                indexOfSelectedOption={indexOfActiveCatalog}
              />
            </Grid>
          ) : null}
          <PromoCodeBanner
            referralCode={cart?.funnelData.params.referralCode}
            authenticated={!!user}
          />
          <Grid container sx={{ marginBottom: '2rem' }}>
            {isLoading ? (
              <Box sx={{ margin: 'auto' }}>
                <Loading />
              </Box>
            ) : (
              <>
                <FilterPanel
                  displayFilters={displayFilters}
                  selectedFilters={selectedFilters}
                  setSelectedFilters={setSelectedFilters}
                />

                <Box
                  sx={{
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    padding: isSmallScreen ? '0 0.35rem' : 0,
                    ...(isSmallScreen
                      ? {
                          justifyContent: 'space-between',
                        }
                      : {
                          justifyContent: 'end',
                          columnGap: '16px',
                        }),
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: '0.875rem',
                    }}
                  >
                    {totalFilteredCatalogItems} results
                  </Typography>
                  {!isSmallScreen && <Divider orientation={'vertical'} />}
                  <div>
                    <SortersDropdown
                      sorters={menuSorters}
                      selectedSorter={selectedSorter}
                      setSelectedSorter={setSelectedSorter}
                    />
                    <Button
                      variant={'contained'}
                      disableElevation
                      onClick={resetSelectedFilters}
                      sx={{
                        marginLeft: '0.35rem',
                        textTransform: 'none',
                        borderRadius: '4px',
                        height: 32,
                        fontSize: '0.875rem',
                        '& .MuiInputBase-input': {
                          padding: '8px',
                        },
                        '& .MuiList-root': {
                          paddingTop: '0',
                        },
                        backgroundColor: 'rgba(0, 0, 0, 0.06)',
                        color: theme.palette.text.primary,
                        fontWeight: 'normal',
                      }}
                    >
                      Reset
                    </Button>
                  </div>
                </Box>

                {onlyOneCatalog || isSmallScreen ? null : (
                  <Grid size={2}>
                    <Box sx={{ display: 'unset' }}>
                      <Box
                        sx={{
                          position: 'sticky',
                          top:
                            (isSmallScreen
                              ? MOBILE_MENU_NAV_BAR_HEIGHT
                              : DESKTOP_MENU_NAV_BAR_HEIGHT) + 2,
                        }}
                      >
                        <CatalogAnchorNav
                          indexOfActiveCatalog={indexOfActiveCatalog}
                          setIndexOfActiveCatalog={(idx: number) => {
                            setIndexOfActiveCatalog(idx);
                            scrollToCatalog(idx, isSmallScreen);
                          }}
                          deliveryDates={
                            cart?.cartDeliveries.map(
                              (delivery) => delivery?.estimatedDeliveryDate,
                            ) ||
                            // This is only needed for the blurred "loading" catalog displayed while the zip modal is open since no cart exists yet.
                            catalogs.map(
                              ({ eligibleDeliveryDates }) =>
                                eligibleDeliveryDates[0],
                            )
                          }
                        />
                      </Box>
                    </Box>
                  </Grid>
                )}
                <Grid size={onlyOneCatalog || isSmallScreen ? 12 : 10}>
                  {catalogsToDisplay.map((c, idx) => {
                    const cartDelivery = cart ? getCartDelivery(cart, c) : null;
                    return (
                      <React.Fragment key={c.id}>
                        <Catalog
                          ref={(el) => {
                            if (el) {
                              catalogRefs.current[idx] = el;
                            }
                          }}
                          catalog={c}
                          cartDelivery={cartDelivery ?? undefined}
                          addMealToCart={(variantId) =>
                            addMealToCart(variantId, cartDelivery?.id)
                          }
                          removeMealFromCart={(variantId) => {
                            cartDelivery
                              ? removeMealWithSoldOutCheck(
                                  variantId,
                                  cartDelivery?.id,
                                )
                              : logger.warn({
                                  msg: 'cartDelivery not found, failed to remove meal from cart',
                                });
                          }}
                          setAsSelected={() => setIndexOfActiveCatalog(idx)}
                          isRecurring={cart?.funnelData.recurring}
                          mealDetailsModalOptions={{
                            enableMealDetailsModal: true,
                            tagFilterType: 'funnel',
                          }}
                          totalCatalogs={catalogs.length}
                          featuredSkuGroups={featuredSkuGroups}
                        />

                        {idx < catalogsToDisplay.length - 1 && (
                          <Divider
                            sx={{
                              width: '100%',
                              marginBottom: '16px',
                              marginTop: '16px',
                            }}
                          />
                        )}
                      </React.Fragment>
                    );
                  })}
                </Grid>
              </>
            )}
            {isZipCodeModalOpen && (
              <ZipCodeModal
                isOpen={isZipCodeModalOpen}
                close={() => setIsZipCodeModalOpen(false)}
                cartFunnelData={{
                  email: cart?.funnelData.email,
                  zip: cart?.funnelData.zip,
                  name: cart?.funnelData.name,
                }}
                handleZipCodeModalSubmit={handleZipCodeModalSubmit}
                isInvalidZipCode={isInvalidZipCode}
                isExternallyLoading={isLoading}
                menuEntryOverride={menuEntryOverride}
                referralCode={
                  cart?.funnelData.params.referralCode ||
                  (router.query[REFERRAL_CODE_PARAM_KEY] as string)
                }
                discountCode={
                  cart?.funnelData.params.discountCode ||
                  (router.query[DISCOUNT_CODE_PARAM_KEY] as string)
                }
                authenticated={!!user}
              />
            )}
          </Grid>
        </Layout>
      </Box>
      <SoldOutConfirmationModal
        open={soldOutDialogOpen}
        onClose={() => setSoldOutDialogOpen(false)}
        onConfirm={async () => {
          if (pendingSoldOutRemoval) {
            await removeMealFromCart(
              pendingSoldOutRemoval.productVariantId,
              pendingSoldOutRemoval.cartDeliveryId,
            );
          }
          setSoldOutDialogOpen(false);
          setPendingSoldOutRemoval(null);
        }}
      />
    </Fragment>
  );
};

export default MenuComponent;
